import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FileJson, X } from 'lucide-react';
import type { BydaYvwSewerAssetFeature, BydaYvwSewerInfo } from '@/services/bydaService';
import './BydaYvwSewerInfoDialog.css';

type BydaYvwSewerInfoDialogProps = {
  info: BydaYvwSewerInfo | null;
  onClose: () => void;
};

type SummaryValue = string | number | boolean | null | undefined;
type CoordinatePair = [number, number];
type ScreenPoint = { x: number; y: number };
type MapRect = { x: number; y: number; width: number; height: number };
type AssetSelectHandler = (asset: BydaYvwSewerAssetFeature) => void;
type AssetRowRegistrar = (assetId: string, element: HTMLTableRowElement | null) => void;
type AssetMapShape = {
  asset: BydaYvwSewerAssetFeature;
  label: string;
  layerClass: string;
  lines: CoordinatePair[][];
  points: CoordinatePair[];
};

const MAP_WIDTH = 1000;
const MAP_HEIGHT = 360;
const MAP_PADDING = 28;

function formatValue(value: SummaryValue, suffix = '') {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '-';
    const formatted = Math.abs(value) >= 100 ? value.toFixed(0) : value.toFixed(2).replace(/\.?0+$/, '');
    return `${formatted}${suffix}`;
  }
  return `${value}${suffix}`;
}

function getSummaryValue(asset: BydaYvwSewerAssetFeature, key: string): SummaryValue {
  return asset.summary?.[key] as SummaryValue;
}

function getAssetLabel(asset: BydaYvwSewerAssetFeature) {
  return String(
    getSummaryValue(asset, 'assetId')
    || getSummaryValue(asset, 'accessPointNumber')
    || getSummaryValue(asset, 'uniqueId')
    || asset.id
  );
}

function toCoordinatePair(value: unknown): CoordinatePair | null {
  if (!Array.isArray(value) || value.length < 2) return null;
  const longitude = Number(value[0]);
  const latitude = Number(value[1]);
  if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return null;
  return [longitude, latitude];
}

function readCoordinateSequence(value: unknown): CoordinatePair[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(toCoordinatePair)
    .filter((point): point is CoordinatePair => Boolean(point));
}

function readLineStringCollection(value: unknown): CoordinatePair[][] {
  if (!Array.isArray(value)) return [];
  return value
    .map(readCoordinateSequence)
    .filter((line) => line.length > 1);
}

function getGeometryType(geometry: Record<string, unknown> | null | undefined) {
  return String(geometry?.type || '').toLowerCase();
}

function getGeometryChildren(geometry: Record<string, unknown> | null | undefined): Array<Record<string, unknown>> {
  const geometries = geometry?.geometries;
  if (!Array.isArray(geometries)) return [];
  return geometries.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object');
}

function getGeometryLines(geometry: Record<string, unknown> | null | undefined): CoordinatePair[][] {
  if (!geometry) return [];
  const type = getGeometryType(geometry);
  const coordinates = geometry.coordinates;

  if (type === 'geometrycollection') {
    return getGeometryChildren(geometry).flatMap(getGeometryLines);
  }
  if (type === 'linestring') {
    const line = readCoordinateSequence(coordinates);
    return line.length > 1 ? [line] : [];
  }
  if (type === 'multilinestring' || type === 'polygon') {
    return readLineStringCollection(coordinates);
  }
  if (type === 'multipolygon' && Array.isArray(coordinates)) {
    return coordinates.flatMap(readLineStringCollection);
  }
  return [];
}

function getGeometryPoints(geometry: Record<string, unknown> | null | undefined): CoordinatePair[] {
  if (!geometry) return [];
  const type = getGeometryType(geometry);
  const coordinates = geometry.coordinates;

  if (type === 'geometrycollection') {
    return getGeometryChildren(geometry).flatMap(getGeometryPoints);
  }
  if (type === 'point') {
    const point = toCoordinatePair(coordinates);
    return point ? [point] : [];
  }
  if (type === 'multipoint') {
    return readCoordinateSequence(coordinates);
  }
  return [];
}

function getAssetLayerClass(asset: BydaYvwSewerAssetFeature) {
  if (asset.layer === 'SEWERPIPES') return 'pipes';
  if (asset.layer === 'SEWERBRANCHES') return 'branches';
  return 'access-points';
}

function getValidBbox(value: unknown): number[] | null {
  if (!Array.isArray(value) || value.length !== 4) return null;
  const [minLon, minLat, maxLon, maxLat] = value.map(Number);
  if (
    [minLon, minLat, maxLon, maxLat].some((item) => !Number.isFinite(item))
    || minLon >= maxLon
    || minLat >= maxLat
  ) {
    return null;
  }
  return [minLon, minLat, maxLon, maxLat];
}

function getCoordinateBounds(coordinates: CoordinatePair[]): number[] | null {
  if (!coordinates.length) return null;
  const longitudes = coordinates.map((point) => point[0]);
  const latitudes = coordinates.map((point) => point[1]);
  return [
    Math.min(...longitudes),
    Math.min(...latitudes),
    Math.max(...longitudes),
    Math.max(...latitudes),
  ];
}

function mergeBounds(...bounds: Array<number[] | null>): number[] | null {
  const validBounds = bounds.filter((bbox): bbox is number[] => Boolean(bbox));
  if (!validBounds.length) return null;
  return [
    Math.min(...validBounds.map((bbox) => bbox[0])),
    Math.min(...validBounds.map((bbox) => bbox[1])),
    Math.max(...validBounds.map((bbox) => bbox[2])),
    Math.max(...validBounds.map((bbox) => bbox[3])),
  ];
}

function projectCoordinate(point: CoordinatePair, bounds: number[]): ScreenPoint {
  const [minLon, minLat, maxLon, maxLat] = bounds;
  const lonSpan = Math.max(maxLon - minLon, 0.000001);
  const latSpan = Math.max(maxLat - minLat, 0.000001);
  return {
    x: MAP_PADDING + ((point[0] - minLon) / lonSpan) * (MAP_WIDTH - MAP_PADDING * 2),
    y: MAP_PADDING + ((maxLat - point[1]) / latSpan) * (MAP_HEIGHT - MAP_PADDING * 2),
  };
}

function projectLine(line: CoordinatePair[], bounds: number[]) {
  return line
    .map((point) => projectCoordinate(point, bounds))
    .map((point) => `${point.x.toFixed(1)},${point.y.toFixed(1)}`)
    .join(' ');
}

function handleAssetKeyDown(
  event: React.KeyboardEvent<SVGGElement | HTMLTableRowElement>,
  asset: BydaYvwSewerAssetFeature,
  onSelectAsset: AssetSelectHandler
) {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  event.preventDefault();
  onSelectAsset(asset);
}

function projectBbox(bbox: number[], bounds: number[]): MapRect {
  const topLeft = projectCoordinate([bbox[0], bbox[3]], bounds);
  const bottomRight = projectCoordinate([bbox[2], bbox[1]], bounds);
  return {
    x: topLeft.x,
    y: topLeft.y,
    width: Math.max(bottomRight.x - topLeft.x, 1),
    height: Math.max(bottomRight.y - topLeft.y, 1),
  };
}

function buildAssetMapData(info: BydaYvwSewerInfo) {
  const subjectBoundaryLines = getGeometryLines(info.search.subjectBoundary || null);
  const shapes: AssetMapShape[] = [
    ...info.pipeAssets,
    ...info.branchAssets,
    ...info.accessPointAssets,
  ].map((asset) => ({
    asset,
    label: getAssetLabel(asset),
    layerClass: getAssetLayerClass(asset),
    lines: getGeometryLines(asset.geometry),
    points: getGeometryPoints(asset.geometry),
  })).filter((shape) => shape.lines.length || shape.points.length);

  const assetCoordinates = shapes.flatMap((shape) => [
    ...shape.points,
    ...shape.lines.flat(),
  ]);
  const assetBounds = getCoordinateBounds(assetCoordinates);
  const subjectBoundaryBounds = getCoordinateBounds(subjectBoundaryLines.flat());
  const searchBbox = getValidBbox(info.search.bbox);
  const subjectBbox = getValidBbox(info.search.originalBbox);
  const bounds = mergeBounds(searchBbox, subjectBbox, subjectBoundaryBounds, assetBounds);

  if (!bounds) {
    return null;
  }

  return {
    shapes,
    bounds,
    subjectBoundaryLines,
    searchRect: searchBbox ? projectBbox(searchBbox, bounds) : null,
    subjectRect: subjectBbox ? projectBbox(subjectBbox, bounds) : null,
  };
}

function renderMetric(label: string, value: SummaryValue, suffix = '') {
  return (
    <div className="byda-yvw-sewer__metric">
      <span>{label}</span>
      <strong>{formatValue(value, suffix)}</strong>
    </div>
  );
}

function renderAssetMap(
  info: BydaYvwSewerInfo,
  selectedAssetId: string | null,
  onSelectAsset: AssetSelectHandler
) {
  const mapData = buildAssetMapData(info);

  return (
    <section className="byda-yvw-sewer__section">
      <div className="byda-yvw-sewer__section-heading">
        <h3>Asset Map</h3>
        <span>{mapData?.shapes.length || 0}</span>
      </div>
      {!mapData || !mapData.shapes.length ? (
        <div className="byda-yvw-sewer__empty">No mappable sewer asset geometry was returned for this enquiry area.</div>
      ) : (
        <div className="byda-yvw-sewer__map-card">
          <svg className="byda-yvw-sewer__map-svg" viewBox={`0 0 ${MAP_WIDTH} ${MAP_HEIGHT}`} role="group" aria-label="YVW sewer asset map">
            <rect className="byda-yvw-sewer__map-bg" x="0" y="0" width={MAP_WIDTH} height={MAP_HEIGHT} />
            {mapData.searchRect ? (
              <rect
                className="byda-yvw-sewer__map-search-bbox"
                x={mapData.searchRect.x}
                y={mapData.searchRect.y}
                width={mapData.searchRect.width}
                height={mapData.searchRect.height}
              />
            ) : null}
            {mapData.subjectBoundaryLines.length ? (
              mapData.subjectBoundaryLines.map((line, index) => (
                <polygon
                  key={`subject-boundary-${index}`}
                  className="byda-yvw-sewer__map-site-boundary"
                  points={projectLine(line, mapData.bounds)}
                >
                  <title>Site property boundary</title>
                </polygon>
              ))
            ) : mapData.subjectRect ? (
              <rect
                className="byda-yvw-sewer__map-subject-bbox"
                x={mapData.subjectRect.x}
                y={mapData.subjectRect.y}
                width={mapData.subjectRect.width}
                height={mapData.subjectRect.height}
              />
            ) : null}
            {mapData.shapes.map((shape) => {
              const isSelected = shape.asset.id === selectedAssetId;
              const className = [
                'byda-yvw-sewer__map-asset',
                `byda-yvw-sewer__map-asset--${shape.layerClass}`,
                isSelected ? 'byda-yvw-sewer__map-asset--selected' : '',
              ].filter(Boolean).join(' ');

              return (
                <g
                  key={shape.asset.id}
                  className={className}
                  role="button"
                  tabIndex={0}
                  aria-label={`${shape.asset.layerLabel}: ${shape.label}`}
                  aria-pressed={isSelected}
                  onClick={() => onSelectAsset(shape.asset)}
                  onKeyDown={(event) => handleAssetKeyDown(event, shape.asset, onSelectAsset)}
                >
                  <title>{`${shape.asset.layerLabel}: ${shape.label}`}</title>
                  {shape.lines.map((line, lineIndex) => (
                    <polyline
                      key={`${shape.asset.id}-hit-line-${lineIndex}`}
                      className="byda-yvw-sewer__map-hit-line"
                      points={projectLine(line, mapData.bounds)}
                      aria-hidden="true"
                    />
                  ))}
                  {shape.lines.map((line, lineIndex) => (
                    <polyline
                      key={`${shape.asset.id}-line-${lineIndex}`}
                      className={`byda-yvw-sewer__map-line byda-yvw-sewer__map-line--${shape.layerClass}`}
                      points={projectLine(line, mapData.bounds)}
                      aria-hidden="true"
                    />
                  ))}
                  {shape.points.map((point, pointIndex) => {
                    const projected = projectCoordinate(point, mapData.bounds);
                    return (
                      <circle
                        key={`${shape.asset.id}-hit-point-${pointIndex}`}
                        className="byda-yvw-sewer__map-hit-point"
                        cx={projected.x}
                        cy={projected.y}
                        r="12"
                        aria-hidden="true"
                      />
                    );
                  })}
                  {shape.points.map((point, pointIndex) => {
                    const projected = projectCoordinate(point, mapData.bounds);
                    return (
                      <circle
                        key={`${shape.asset.id}-point-${pointIndex}`}
                        className={`byda-yvw-sewer__map-point byda-yvw-sewer__map-point--${shape.layerClass}`}
                        cx={projected.x}
                        cy={projected.y}
                        r={isSelected ? 7 : 5}
                        aria-hidden="true"
                      />
                    );
                  })}
                </g>
              );
            })}
          </svg>
          <div className="byda-yvw-sewer__map-footer">
            <div className="byda-yvw-sewer__map-legend">
              <span><i className="byda-yvw-sewer__legend-swatch byda-yvw-sewer__legend-swatch--pipes" /> Pipes</span>
              <span><i className="byda-yvw-sewer__legend-swatch byda-yvw-sewer__legend-swatch--branches" /> Branches</span>
              <span><i className="byda-yvw-sewer__legend-swatch byda-yvw-sewer__legend-swatch--access-points" /> Access points</span>
              <span><i className="byda-yvw-sewer__legend-swatch byda-yvw-sewer__legend-swatch--subject" /> Site boundary</span>
            </div>
            <div className="byda-yvw-sewer__map-caption">Rendered from YVW WFS GeoJSON held in memory for this lookup.</div>
          </div>
        </div>
      )}
    </section>
  );
}

function renderPipeRows(
  assets: BydaYvwSewerAssetFeature[],
  selectedAssetId: string | null,
  onSelectAsset: AssetSelectHandler,
  registerAssetRow: AssetRowRegistrar
) {
  if (!assets.length) {
    return <div className="byda-yvw-sewer__empty">No sewer pipes returned for this enquiry area.</div>;
  }

  return (
    <div className="byda-yvw-sewer__table-wrap">
      <table className="byda-yvw-sewer__table">
        <thead>
          <tr>
            <th>Asset</th>
            <th>Size</th>
            <th>US IL</th>
            <th>DS IL</th>
            <th>US Depth</th>
            <th>DS Depth</th>
            <th>Avg Depth</th>
            <th>Material</th>
            <th>Quality</th>
          </tr>
        </thead>
        <tbody>
          {assets.map((asset) => (
            <tr
              key={asset.id}
              ref={(element) => registerAssetRow(asset.id, element)}
              className={asset.id === selectedAssetId ? 'byda-yvw-sewer__table-row--selected' : undefined}
              tabIndex={0}
              onClick={() => onSelectAsset(asset)}
              onKeyDown={(event) => handleAssetKeyDown(event, asset, onSelectAsset)}
            >
              <td>{getAssetLabel(asset)}</td>
              <td>{formatValue(getSummaryValue(asset, 'nominalPipeSize'), ' mm')}</td>
              <td>{formatValue(getSummaryValue(asset, 'upstreamInvert'))}</td>
              <td>{formatValue(getSummaryValue(asset, 'downstreamInvert'))}</td>
              <td>{formatValue(getSummaryValue(asset, 'estimatedUpstreamDepth'), ' m')}</td>
              <td>{formatValue(getSummaryValue(asset, 'estimatedDownstreamDepth'), ' m')}</td>
              <td>{formatValue(getSummaryValue(asset, 'estimatedAverageDepth'), ' m')}</td>
              <td>{formatValue(getSummaryValue(asset, 'pipeMaterial'))}</td>
              <td>{formatValue(getSummaryValue(asset, 'dataQualityRating'))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderBranchRows(
  assets: BydaYvwSewerAssetFeature[],
  selectedAssetId: string | null,
  onSelectAsset: AssetSelectHandler,
  registerAssetRow: AssetRowRegistrar
) {
  if (!assets.length) {
    return <div className="byda-yvw-sewer__empty">No sewer branches returned for this enquiry area.</div>;
  }

  return (
    <div className="byda-yvw-sewer__table-wrap">
      <table className="byda-yvw-sewer__table">
        <thead>
          <tr>
            <th>Asset</th>
            <th>Size</th>
            <th>Invert</th>
            <th>Depth to IL</th>
            <th>Pipe Depth</th>
            <th>Top of Stack</th>
            <th>Material</th>
            <th>Quality</th>
          </tr>
        </thead>
        <tbody>
          {assets.map((asset) => (
            <tr
              key={asset.id}
              ref={(element) => registerAssetRow(asset.id, element)}
              className={asset.id === selectedAssetId ? 'byda-yvw-sewer__table-row--selected' : undefined}
              tabIndex={0}
              onClick={() => onSelectAsset(asset)}
              onKeyDown={(event) => handleAssetKeyDown(event, asset, onSelectAsset)}
            >
              <td>{getAssetLabel(asset)}</td>
              <td>{formatValue(getSummaryValue(asset, 'branchSize'))}</td>
              <td>{formatValue(getSummaryValue(asset, 'invertLevel'))}</td>
              <td>{formatValue(getSummaryValue(asset, 'depthToBranchEndpointInvert'), ' m')}</td>
              <td>{formatValue(getSummaryValue(asset, 'estimatedDepthSewerPipe'), ' m')}</td>
              <td>{formatValue(getSummaryValue(asset, 'levelTopOfStack'))}</td>
              <td>{formatValue(getSummaryValue(asset, 'branchMaterial'))}</td>
              <td>{formatValue(getSummaryValue(asset, 'dataQualityRating'))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderAccessPointRows(
  assets: BydaYvwSewerAssetFeature[],
  selectedAssetId: string | null,
  onSelectAsset: AssetSelectHandler,
  registerAssetRow: AssetRowRegistrar
) {
  if (!assets.length) {
    return null;
  }

  return (
    <section className="byda-yvw-sewer__section">
      <div className="byda-yvw-sewer__section-heading">
        <h3>Sewer Access Points</h3>
        <span>{assets.length}</span>
      </div>
      <div className="byda-yvw-sewer__table-wrap">
        <table className="byda-yvw-sewer__table">
          <thead>
            <tr>
              <th>Asset</th>
              <th>Function</th>
              <th>Cover Level</th>
              <th>Size</th>
              <th>Material</th>
              <th>Quality</th>
            </tr>
          </thead>
          <tbody>
            {assets.map((asset) => (
              <tr
                key={asset.id}
                ref={(element) => registerAssetRow(asset.id, element)}
                className={asset.id === selectedAssetId ? 'byda-yvw-sewer__table-row--selected' : undefined}
                tabIndex={0}
                onClick={() => onSelectAsset(asset)}
                onKeyDown={(event) => handleAssetKeyDown(event, asset, onSelectAsset)}
              >
                <td>{getAssetLabel(asset)}</td>
                <td>{formatValue(getSummaryValue(asset, 'accessPointFunction'))}</td>
                <td>{formatValue(getSummaryValue(asset, 'coverLevel'))}</td>
                <td>{formatValue(getSummaryValue(asset, 'nominalInternalSize'), ' mm')}</td>
                <td>{formatValue(getSummaryValue(asset, 'structureMaterial'))}</td>
                <td>{formatValue(getSummaryValue(asset, 'dataQualityRating'))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function BydaYvwSewerInfoDialog({ info, onClose }: BydaYvwSewerInfoDialogProps) {
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const assetRowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
  const jsonText = useMemo(() => (
    info ? JSON.stringify(info, null, 2) : ''
  ), [info]);

  const registerAssetRow = useCallback<AssetRowRegistrar>((assetId, element) => {
    if (element) {
      assetRowRefs.current[assetId] = element;
    } else {
      delete assetRowRefs.current[assetId];
    }
  }, []);

  const handleSelectAsset = useCallback<AssetSelectHandler>((asset) => {
    setSelectedAssetId(asset.id);
  }, []);

  useEffect(() => {
    setSelectedAssetId(null);
    assetRowRefs.current = {};
  }, [info?.bydaEnquiryId]);

  useEffect(() => {
    if (!selectedAssetId) return;
    const row = assetRowRefs.current[selectedAssetId];
    if (typeof row?.scrollIntoView === 'function') {
      row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedAssetId]);

  useEffect(() => {
    if (!info) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [info, onClose]);

  if (!info) return null;

  return (
    <div className="byda-yvw-sewer" role="dialog" aria-modal="true" aria-label="YVW sewer information" onClick={onClose}>
      <div className="byda-yvw-sewer__panel" onClick={(event) => event.stopPropagation()}>
        <header className="byda-yvw-sewer__header">
          <div className="byda-yvw-sewer__title-wrap">
            <div className="byda-yvw-sewer__eyebrow">Yarra Valley Water WFS</div>
            <h2 className="byda-yvw-sewer__title">Subject Property Sewer Info</h2>
            <div className="byda-yvw-sewer__subtitle">
              {[info.project.projectNumber, info.project.siteAddress, info.project.suburb].filter(Boolean).join(' - ') || `BYDA ${info.bydaEnquiryId}`}
            </div>
          </div>
          <button type="button" className="byda-yvw-sewer__icon-button" onClick={onClose} aria-label="Close sewer information" title="Close">
            <X size={20} aria-hidden="true" />
          </button>
        </header>

        <div className="byda-yvw-sewer__body">
          <div className="byda-yvw-sewer__metric-grid">
            {renderMetric('Sewer pipes', info.counts.sewerPipes)}
            {renderMetric('Branches', info.counts.sewerBranches)}
            {renderMetric('Access points', info.counts.sewerAccessPoints)}
            {renderMetric('Search buffer', info.search.bufferMeters, ' m')}
          </div>

          {info.warnings.length ? (
            <div className="byda-yvw-sewer__warning">{info.warnings.join(' ')}</div>
          ) : null}

          {renderAssetMap(info, selectedAssetId, handleSelectAsset)}

          <section className="byda-yvw-sewer__section">
            <div className="byda-yvw-sewer__section-heading">
              <h3>Sewer Pipes</h3>
              <span>{info.pipeAssets.length}</span>
            </div>
            {renderPipeRows(info.pipeAssets, selectedAssetId, handleSelectAsset, registerAssetRow)}
          </section>

          <section className="byda-yvw-sewer__section">
            <div className="byda-yvw-sewer__section-heading">
              <h3>Sewer Branches</h3>
              <span>{info.branchAssets.length}</span>
            </div>
            {renderBranchRows(info.branchAssets, selectedAssetId, handleSelectAsset, registerAssetRow)}
          </section>

          {renderAccessPointRows(info.accessPointAssets, selectedAssetId, handleSelectAsset, registerAssetRow)}

          <details className="byda-yvw-sewer__json">
            <summary>
              <FileJson size={16} aria-hidden="true" />
              In-memory JSON
            </summary>
            <pre>{jsonText}</pre>
          </details>

          <div className="byda-yvw-sewer__disclaimer">{info.source.disclaimer}</div>
        </div>
      </div>
    </div>
  );
}
